import { Card, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { format, formatDistanceToNow } from 'date-fns'
import {
	ChevronUp,
	MoreVertical,
	Lightbulb,
	Trash2,
	Lock,
	Loader2,
} from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useProgramContext } from '@/context/ProgramContext'
import { canDeleteFeedback } from '@/lib/programPermissions'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useState } from 'react'
import { FeatureRequest } from '@/lib/services/feedback'
import { apiClient } from '@/lib/api'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { launchCelebrationConfetti } from '@/utils/confetti'
import { useTranslation } from '@/hooks/useTranslation'

interface FeatureRequestGridProps {
	features: FeatureRequest[]
	isLoading: boolean
	onFeatureSelect?: (featureId: string | null) => void
	className?: string
	isEmpty?: boolean
	totalCount?: number
	isPro?: boolean
	hiddenCount?: number
	itemsPerPage?: number
	viewableLimit?: number
}

interface FeatureCardProps {
	feature: FeatureRequest
	onUpvote: () => void
	onStatusChange: (newStatus: string) => void
	onDelete: () => void
	onViewDetails: () => void
	isBlurred?: boolean
	canDelete?: boolean
}

const getStatusOptions = (t: (key: string) => string) => [
	{ label: t('featureRequestGrid.statusNew'), value: 'New' },
	{ label: t('featureRequestGrid.statusUnderConsideration'), value: 'Under Consideration' },
	{ label: t('featureRequestGrid.statusPlanned'), value: 'Planned' },
	{ label: t('featureRequestGrid.statusInProgress'), value: 'In Progress' },
	{ label: t('featureRequestGrid.statusLaunched'), value: 'Launched' },
	{ label: t('featureRequestGrid.statusDeclined'), value: 'Declined' },
]

export const getStatusColorClasses = (status?: string) => {
	switch (status?.toLowerCase()) {
		case 'new':
			return {
				bg: 'bg-blue-100 dark:bg-blue-900/50',
				text: 'text-blue-700 dark:text-blue-300',
				border: 'border-blue-300 dark:border-blue-700',
			}
		case 'under consideration':
			return {
				bg: 'bg-purple-100 dark:bg-purple-900/50',
				text: 'text-purple-700 dark:text-purple-300',
				border: 'border-purple-300 dark:border-purple-700',
			}
		case 'planned':
			return {
				bg: 'bg-yellow-100 dark:bg-yellow-900/50',
				text: 'text-yellow-700 dark:text-yellow-300',
				border: 'border-yellow-300 dark:border-yellow-700',
			}
		case 'in progress':
			return {
				bg: 'bg-orange-100 dark:bg-orange-900/50',
				text: 'text-orange-700 dark:text-orange-300',
				border: 'border-orange-300 dark:border-orange-700',
			}
		case 'launched':
			return {
				bg: 'bg-green-100 dark:bg-green-900/50',
				text: 'text-green-700 dark:text-green-300',
				border: 'border-green-300 dark:border-green-700',
			}
		case 'declined':
			return {
				bg: 'bg-rose-100 dark:bg-rose-900/50',
				text: 'text-rose-700 dark:text-rose-300',
				border: 'border-rose-300 dark:border-rose-700',
			}
		default:
			return {
				bg: 'bg-gray-100 dark:bg-gray-800/50',
				text: 'text-gray-700 dark:text-gray-400',
				border: 'border-gray-300 dark:border-gray-600',
			}
	}
}

const FeatureCard: React.FC<FeatureCardProps & { t: (key: string) => string }> = ({
	feature,
	onUpvote,
	onStatusChange,
	onDelete,
	onViewDetails,
	isBlurred,
	canDelete = true,
	t,
}) => {
	const statusColors = getStatusColorClasses(feature.status)
	const STATUS_OPTIONS = getStatusOptions(t)
	return (
		<Card
			className={cn(
				'flex flex-row transition-shadow hover:shadow-lg cursor-pointer p-2',
				isBlurred && 'filter blur-sm opacity-60 pointer-events-none select-none'
			)}
			onClick={!isBlurred ? onViewDetails : undefined}>
			<div className="flex-1 p-2">
				<CardTitle
					className={cn(
						'text-lg mb-2 group-hover:text-primary transition-colors'
					)}>
					{feature.title || t('featureRequestGrid.untitledFeature')}
				</CardTitle>

				<p className="text-sm text-muted-foreground line-clamp-2">
					{feature.description || t('featureRequestGrid.noDescriptionProvided')}
				</p>

				{/* Status Badge and Submitter */}
				<div className="mt-3 flex items-center justify-between">
					<div className="flex items-center gap-2">
						<Badge
							variant="outline"
							className={cn(
								'capitalize text-xs font-medium',
								statusColors.bg,
								statusColors.text,
								statusColors.border
							)}>
							{feature.status?.replace('_', ' ') || 'N/A'}
						</Badge>
						{(feature.submitter_name || feature.submitter_email) && (
							<span className="text-xs text-muted-foreground">
								{t('featureRequestGrid.by')} {feature.submitter_name}
								{feature.submitter_name && feature.submitter_email && ' ('}
								{feature.submitter_email && (
									<span className="font-medium">{feature.submitter_email}</span>
								)}
								{feature.submitter_name && feature.submitter_email && ')'}
								{!feature.submitter_name && feature.submitter_email}
							</span>
						)}
					</div>
					<Tooltip>
						<TooltipTrigger asChild>
							<div className="text-xs text-muted-foreground">
								{formatDistanceToNow(
									new Date(feature.created_at || new Date()),
									{
										addSuffix: true,
									}
								)}
							</div>
						</TooltipTrigger>
						<TooltipContent>
							<p className="text-xs text-muted-foreground">
								{format(
									new Date(feature.created_at || new Date()),
									'MMM d, yyyy hh:mm a zzz'
								)}
							</p>
						</TooltipContent>
					</Tooltip>
				</div>
			</div>

			{/* Right side - Actions and Upvotes */}
			<div className="flex flex-col items-end justify-between min-w-[120px] border-l border-border/50 pr-2">
				{/* Menu Actions */}
				<div className="self-end">
					{!isBlurred && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									className="h-8 w-8 p-0"
									onClick={(e) => e.stopPropagation()}>
									<MoreVertical className="h-4 w-4" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={(e) => {
									e.stopPropagation()
									onViewDetails()
								}}>
									{t('featureRequestGrid.viewDetails')}
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuSub>
									<DropdownMenuSubTrigger>{t('featureRequestGrid.changeStatus')}</DropdownMenuSubTrigger>
									<DropdownMenuSubContent>
										{STATUS_OPTIONS.filter(
											(o) => o.value !== feature.status
										).map((o) => (
											<DropdownMenuItem
												key={o.value}
												onClick={(e) => {
													e.stopPropagation()
													onStatusChange(o.value)
												}}>
												{o.label}
											</DropdownMenuItem>
										))}
									</DropdownMenuSubContent>
								</DropdownMenuSub>
								{canDelete && (
									<>
										<DropdownMenuSeparator />
										<DropdownMenuItem
											className="text-destructive focus:text-destructive focus:bg-destructive/10"
											onClick={(e) => {
												e.stopPropagation()
												onDelete()
											}}>
											<Trash2 className="mr-2 h-4 w-4" /> {t('featureRequestGrid.delete')}
										</DropdownMenuItem>
									</>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>

				<div className="text-center">
					<Button
						variant="ghost"
						size="sm"
						className={cn(
							'h-auto p-3 flex flex-col items-center gap-1 bg-primary/5 hover:bg-primary/15 border border-primary/20 rounded-lg transition-colors',
							!isBlurred && 'cursor-pointer'
						)}
						onClick={(e) => {
							e.stopPropagation()
							if (!isBlurred) onUpvote()
						}}
						disabled={isBlurred}>
						<ChevronUp className="h-5 w-5 text-primary" />
						<span className="text-sm font-semibold text-primary">
							{feature.upvote_count || 0}
						</span>
					</Button>
				</div>
			</div>
		</Card>
	)
}

export const FeatureRequestGrid: React.FC<FeatureRequestGridProps> = ({
	features = [],
	isLoading,
	className,
	onFeatureSelect,
	isEmpty,
	totalCount = 0,
	isPro = true,
	hiddenCount = 0,
	itemsPerPage = 9,
	viewableLimit = -1,
}) => {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const { currentProgramAccessLevel } = useProgramContext()
	const canDelete = canDeleteFeedback(currentProgramAccessLevel)
	const [showDeleteDialog, setShowDeleteDialog] = useState(false)
	const [featureToDelete, setFeatureToDelete] = useState<FeatureRequest | null>(
		null
	)

	const deleteMutation = useMutation({
		mutationFn: async (featureId: string) => {
			await apiClient(`/api/features/protected/${featureId}`, {
				method: 'DELETE',
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['features'] })
			queryClient.invalidateQueries({ queryKey: ['dashboardStats'] })
			queryClient.invalidateQueries({ queryKey: ['recentActivity'] })
			// Also invalidate public roadmap queries since feature deletion affects public roadmap
			queryClient.invalidateQueries({ queryKey: ['publicRoadmap'] })
			setShowDeleteDialog(false)
			setFeatureToDelete(null)
		},
		onError: (error: any) => {
			console.error('Delete feature error:', error)
		},
	})

	const upvoteMutation = useMutation({
		mutationFn: async (featureId: string) => {
			await apiClient(`/api/features/${featureId}/upvote`, {
				method: 'POST',
			})
		},
		onSuccess: (_, featureId) => {
			queryClient.invalidateQueries({ queryKey: ['features'] })
			queryClient.invalidateQueries({ queryKey: ['feature', featureId] })
		},
		onError: (error: any) => {
			console.error('Upvote error:', error)
		},
	})

	const statusMutation = useMutation({
		mutationFn: async ({
			featureId,
			status,
		}: {
			featureId: string
			status: string
		}) => {
			await apiClient(`/api/features/protected/${featureId}`, {
				method: 'PUT',
				body: JSON.stringify({ status }),
			})
		},
		onSuccess: (_, { featureId }) => {
			queryClient.invalidateQueries({ queryKey: ['features'] })
			queryClient.invalidateQueries({ queryKey: ['feature', featureId] })
			// Also invalidate public roadmap queries since feature status updates affect public roadmap
			queryClient.invalidateQueries({ queryKey: ['publicRoadmap'] })
		},
		onError: (error: any) => {
			console.error('Status update error:', error)
		},
	})

	const handleFeatureClick = (featureId: string) => {
		if (onFeatureSelect) onFeatureSelect(featureId)
	}

	const handleDeleteClick = (feature: FeatureRequest) => {
		setFeatureToDelete(feature)
		setShowDeleteDialog(true)
	}

	const confirmDelete = () => {
		if (featureToDelete) deleteMutation.mutate(featureToDelete.id)
	}

	const handleStatusChange = (feature: FeatureRequest, newStatus: string) => {
		
		// Trigger confetti if changing to "Launched" from any other status
		if (newStatus === 'Launched' && feature.status !== 'Launched') {
			try {
				launchCelebrationConfetti()
			} catch (error) {
				console.error('Error launching confetti from grid:', error)
			}
		}
		
		// Update the status
		statusMutation.mutate({
			featureId: feature.id,
			status: newStatus,
		})
	}

	const displayedFeatures = features || []

	let placeholdersNeeded = 0
	if (!isPro && hiddenCount > 0) {
		placeholdersNeeded = Math.min(
			itemsPerPage - displayedFeatures.length,
			hiddenCount,
			3
		)
	}
	placeholdersNeeded = Math.max(0, placeholdersNeeded)

	if (isLoading && displayedFeatures.length === 0) {
		return (
			<div className={cn('space-y-4', className)}>
				{[...Array(itemsPerPage)].map((_, i) => (
					<Card key={`skeleton-${i}`} className="flex flex-row h-[140px]">
						<div className="flex-1 p-6">
							<Skeleton className="h-6 w-3/4 mb-2" />
							<Skeleton className="h-4 w-full mb-1" />
							<Skeleton className="h-4 w-2/3 mb-3" />
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-2">
									<Skeleton className="h-5 w-24" />
									<Skeleton className="h-4 w-16" />
								</div>
								<Skeleton className="h-4 w-16" />
							</div>
						</div>
						<div className="min-w-[120px] flex flex-col items-center justify-between border-l border-border/50 p-4">
							<div className="self-end">
								<Skeleton className="h-8 w-8" />
							</div>
							<div className="text-center">
								<Skeleton className="h-16 w-16 rounded-lg bg-primary/10" />
							</div>
						</div>
					</Card>
				))}
			</div>
		)
	}

	if (isEmpty && placeholdersNeeded === 0) {
		return (
			<div className="text-center py-12">
				<Lightbulb className="mx-auto h-12 w-12 text-muted-foreground" />
				<h3 className="mt-4 text-lg font-medium text-foreground">
					{t('featureRequestGrid.noFeatureRequestsFound')}
				</h3>
				<p className="mt-1 text-sm text-muted-foreground">
					{t('featureRequestGrid.tryAdjustingFilters')}
				</p>
				{!isPro && totalCount > 0 && hiddenCount === totalCount && (
					<p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
						{t('featureRequestGrid.allRequestsHidden', { totalCount })}
						<Button
							variant="link"
							className="p-0 h-auto text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline ml-1"
							onClick={() => navigate('/settings?tab=billing')}>
							{t('featureRequestGrid.upgradeToView')}
						</Button>
					</p>
				)}
			</div>
		)
	}

	return (
		<>
			<div className={cn('space-y-4 relative', className)}>
				{displayedFeatures.map((feature) => (
					<FeatureCard
						key={feature.id}
						feature={feature}
						onUpvote={() => upvoteMutation.mutate(feature.id)}
						onStatusChange={(newStatus) =>
							handleStatusChange(feature, newStatus)
						}
						onDelete={() => handleDeleteClick(feature)}
						onViewDetails={() => handleFeatureClick(feature.id)}
						canDelete={canDelete}
						t={t}
					/>
				))}
				{!isPro &&
					placeholdersNeeded > 0 &&
					Array.from({ length: placeholdersNeeded }).map((_, index) =>
						index === 0 &&
						hiddenCount > 0 &&
						displayedFeatures.length < itemsPerPage ? (
							<Card
								key={`placeholder-action-${index}`}
								className="flex flex-row h-[140px] items-center justify-center p-6 bg-slate-50/70 dark:bg-slate-800/30 border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600 transition-colors">
								<div className="flex items-center gap-4">
									<Lock className="h-10 w-10 text-slate-400 dark:text-slate-500" />
									<div className="flex-1">
										<p className="text-sm text-muted-foreground mb-1">
											{t('featureRequestGrid.moreFeatureRequestsHidden', { count: hiddenCount })}
										</p>
										<p className="text-xs text-muted-foreground">
											{t('featureRequestGrid.upgradePlanToViewAll')}
										</p>
									</div>
									<Button
										variant="default"
										size="default"
										className="upgrade-button bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-md hover:shadow-lg transition-shadow"
										onClick={(e) => {
											e.stopPropagation()
											navigate('/settings?tab=billing')
										}}>
										{t('featureRequestGrid.unlockAllFeatures')}
									</Button>
								</div>
							</Card>
						) : (
							<></>
						)
					)}
			</div>
			<AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('featureRequestGrid.areYouAbsolutelySure')}</AlertDialogTitle>
						<AlertDialogDescription>
							{t('featureRequestGrid.deleteFeatureRequestConfirmation')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={deleteMutation.isPending}>
							{t('featureRequestGrid.cancel')}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={confirmDelete}
							disabled={deleteMutation.isPending}
							className="bg-destructive hover:bg-destructive/90">
							{deleteMutation.isPending ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<Trash2 className="mr-2 h-4 w-4" />
							)}{' '}
							{t('featureRequestGrid.delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
